import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { Helmet } from 'react-helmet';
import GoogleMapReact from 'google-map-react';
import { useDispatch } from 'react-redux';

import { Grid, Box, Typography, Popover, Slider, Tooltip, IconButton } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridValueFormatterParams,
  GridRenderCellParams,
  GridEventListener,
  useGridApiContext,
  useGridApiEventHandler,
  GridRowParams,
} from '@mui/x-data-grid';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import RoomIcon from '@mui/icons-material/Room';
import QuestionMarkOutlinedIcon from '@mui/icons-material/QuestionMarkOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import { LoadingCard, MarketRecommendationPointer } from '../../../HOC';
import {
  processPolygon,
  applySmartBounds,
  handleMarketRecommendationClick,
  formatCurrency,
  convertCurrency,
  makeDownloadableBlob,
  checkIfGooglePlacesSessionValid,
  getCentroid,
  geocodeAddress,
  areArraysEqual,
  getColorFromSizeLevel,
} from 'src/utils/functions';
import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  POINTER_GREEN,
  sampleDiscoveryAddress,
  mapOptions,
  COLOR_BLACK,
  COLOR_WHITE,
  exchangeRates,
  COLOR_GREY,
  DISCOVERY_TOOL_VIDEO_TUTORIAL_LINK,
  BACKGROUND_COLOR,
  COLOR_ORANGE,
  LIGHT_GREY,
  CountryCodeWithCurrency,
  airbticsProCountries,
  WORDPRESS_TO_DISCOVERY_KEY,
} from 'src/const';
import {
  getNearbyRecommendedMarkets,
  getNearbyRecommendedMarketsLoadingEnd,
} from 'src/store/Dashboard/actions';
import {
  Bounds,
  Coordinates,
  DiscoveryInputMarket,
  DiscoveryTableData,
  NearbyRecommendedMarket,
} from 'src/interfaces/idashboard';
import { DiscoverDashboardPropTypes } from 'src/interfaces/iproptypes';
import { OutlinedRectangularButton } from 'src/App/Components/Buttons';
import { DecisionDialog, SearchbarAutocomplete } from 'src/App/Components';
import { SelecFilterChip } from 'src/App/Components/Chips';
import IndividualOnBoardingPage from 'src/App/Pages/IndividualOnBoardingPage';
import '../../Dashboard.scss';
import greyMapBackground from 'src/App/assets/greyMapBackground.jpg';
import { User } from 'src/interfaces/iuser';
import { useLocation } from 'react-router-dom';
import AirbticsProDiscovery from 'src/App/Pages/AirbticsProDiscovery';
import RedirectModal from 'src/App/Modals/RedirectModalDashboard';
import { GOOGLE_MAPS_API_KEY } from 'src/keys';

const MAP_FILL_COLOR = POINTER_GREEN;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mapContainer: {
      [theme.breakpoints.up('xs')]: {
        height: 580,
      },
      [theme.breakpoints.up(900)]: {
        height: '80vh',
      },
      borderRadius: '20px',
      overflow: 'hidden',
    },
  })
);

const RoomIconMarker = ({ lat, lng }) => {
  return (
    <RoomIcon
      sx={{
        fontSize: 40,
        color: COLOR_BLACK,
        cursor: 'pointer',
      }}
    />
  );
};

const SubscriptionPromptOverlay = ({
  setSubscriptionModalOpen,
  setLoginModalOpen,
  isUserLoggedIn,
}) => {
  return (
    <div
      style={{
        position: 'absolute',
        top: '300px',
        left: '0',
        width: '100%',
        backgroundColor: 'transparent',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <OutlinedRectangularButton
        height="50px"
        width="150px"
        colors={{
          backgroundColor: COLOR_WHITE,
          color: COLOR_PRIMARY_DARK,
          borderColor: 'rgba(58, 175, 169, 0.54)',
        }}
        text="SUBSCRIBE"
        handleOnClick={() => {
          if (!isUserLoggedIn) {
            setLoginModalOpen(true);
          } else {
            setSubscriptionModalOpen(true);
          }
        }}
        isFocused={true}
        fontWeight="bold"
        fontSize="1.2rem"
      />
      <Typography
        sx={{
          marginLeft: '10px',
          fontSize: '1.2rem',
          fontWeight: 'bold',
        }}
      >
        to Unlock!
      </Typography>
    </div>
  );
};

const DiscoveryTableMarketSizeMap = ['big', 'medium', 'small'];

const DiscoveryDashboard = ({
  nearbyRecommendedMarkets,
  currency,
  setCurrency,
  googleAutocompleteSessionToken,
  getGoogleAutocompleteSessionToken,
  clearGoogleAutocompleteSessionToken,
  googleAutocompleteSessionTokenTimeStamp,
  userCountryCode,
  user,
  setSubscriptionModalOpen,
  loginModalOpen,
  setLoginModalOpen,
  handleMarketSidebarClick,
  isLoggedIn,
}: DiscoverDashboardPropTypes) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMdScreenSize = useMediaQuery(theme.breakpoints.down('md'));

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchValue = queryParams.get('search');
  const countryParamsValue = queryParams.get('country');
  const isFromWordpress = queryParams.get('source');

  const currentPathNameArray = window.location.pathname.split('/');
  const countryCodeFromURL = currentPathNameArray[currentPathNameArray.length - 1]?.toUpperCase();
  const airbticsProCountryCode = ['US', 'UK', 'CA', 'AU'].includes(countryCodeFromURL)
    ? countryCodeFromURL
    : 'US';

  const loader = new Loader({
    apiKey: GOOGLE_MAPS_API_KEY,
    id: '__googleMapsScriptId',
    version: 'weekly',
    libraries: ['drawing', 'places', 'visualization'],
  });

  const sampleMarket = {
    district: splitDistrictAndCountryFromAddress(sampleDiscoveryAddress).district,
    country: splitDistrictAndCountryFromAddress(sampleDiscoveryAddress).country,
    centerLat: 25.7616798,
    centerLng: -80.19179,
  };

  const isUserLoggedIn = user.email !== 'Trial';

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [loadingState, setLoadingState] = useState(false);
  const [searchBarState, setSearchBarState] = useState('');
  const loading: boolean = open && loadingState;
  const emptySearchBar: boolean = open && searchBarState.length === 0;
  const [searchBarValue, setSearchBarValue] = useState<string[]>([]);
  const [inputMarkets, setInputMarkets] = useState<DiscoveryInputMarket[]>([]);
  const [centroid, setCentroid] = useState({ lat: 25.7616798, lng: -80.19179 });
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [searchNotSupportedWarningModalOpen, setSearchNotSupportedWarningModalOpen] = useState(
    false
  );
  const [rows, setRows] = useState<DiscoveryTableData[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const filterOpen = Boolean(anchorEl);
  const [bedroomsFilterRange, setBedroomsFilterRange] = useState([1, 5]);
  const [distanceFilterRange, setDistanceFilterRange] = useState([0, 700]);
  const [marketSizeFilter, setMarketSizeFilter] = useState({
    small: true,
    medium: true,
    big: false,
  });
  const [marketTypeFilter, setMarketTypeFilter] = useState({
    vacation: true,
    urban: false,
  });
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filteredRows, setFilteredRows] = useState<DiscoveryTableData[]>([]);
  const [hoveredRow, setHoveredRow] = useState<{
    center: null | { lat: number; lng: number };
    anchorEl: null | HTMLElement;
  }>({
    center: null,
    anchorEl: null,
  });
  const [searchbarFocus, setSearchbarFocus] = useState(false);
  const [distanceTooltipText, setDistanceTooltipText] = useState('');
  const [mapCopy, setMapCopy] = useState<any | null>(null);
  const [polygonCopies, setPolygonCopies] = useState<any | null>(null);
  const [currentCountry, setCurrentCountry] = useState<string>('');

  const lastPromise = useRef();

  useEffect(() => {
    if (isFromWordpress != WORDPRESS_TO_DISCOVERY_KEY) {
      setLoginModalOpen(true);
    }
  }, [isFromWordpress]);

  useEffect(() => {
    if (
      userCountryCode !== 'US' &&
      userCountryCode !== 'GB' &&
      userCountryCode !== 'CA' &&
      userCountryCode !== 'AU' &&
      isFromWordpress !== WORDPRESS_TO_DISCOVERY_KEY &&
      isUserLoggedIn
    ) {
      setSearchNotSupportedWarningModalOpen(true);
    }
  }, [userCountryCode, isUserLoggedIn]);

  useEffect(() => {
    if (inputMarkets.length > 0) {
      dispatch(getNearbyRecommendedMarkets(inputMarkets, 20, lastPromise));
    }
  }, [inputMarkets]);

  useEffect(() => {
    const fetchMarketSugessionsBySearchQuery = async (searchValue: string) => {
      console.log(
        'Search Query, Country Query:: ',
        searchValue,
        countryParamsValue,
        airbticsProCountryCode
      );
      setSearchNotSupportedWarningModalOpen(false);
      setSearchBarValue([searchValue]);
      await discoverMarkets([searchValue]);
    };

    const newCurrency = airbticsProCountryCode && CountryCodeWithCurrency[airbticsProCountryCode];
    setCurrency(newCurrency);

    searchValue && window.google && fetchMarketSugessionsBySearchQuery(searchValue);
  }, [searchValue, window.google]);

  const handleInputChange = useCallback(
    async (event, value: string) => {
      setSearchBarState(value);
      setLoadingState(true);

      let values: string[] = [];

      let sessionToken = googleAutocompleteSessionToken;
      const isGooglePlacesNeeded: boolean = !checkIfGooglePlacesSessionValid(
        googleAutocompleteSessionTokenTimeStamp
      );
      if (isGooglePlacesNeeded) {
        sessionToken = await getGoogleAutocompleteSessionToken(loader);
      }

      const google = await loader.load();
      const service = new google.maps.places.AutocompleteService();

      try {
        if (value.length > 0) {
          await service.getPlacePredictions(
            { input: `${value}`, sessionToken: sessionToken },
            (results, status) => {
              if (status == google.maps.places.PlacesServiceStatus.OK && results) {
                results.forEach(result => {
                  values.push(result.description);
                });
              }
            }
          );
        }
      } catch (error) {
        console.log('error in estimator autocomplete ', error);
      }

      setOptions(e => {
        let tempAddressObjs: string[] = [...values];
        // tempAddressObjs.push({ address: 'Set location on map', isDefaultOption: true });
        return tempAddressObjs;
      });

      setLoadingState(false);
    },
    [googleAutocompleteSessionToken]
  );

  const handleSearchChange = (value: string[]) => {
    setSearchBarValue(value);
    clearGoogleAutocompleteSessionToken();
  };

  const discoverMarkets = useCallback(async (inputAddresses: string[]) => {
    if (inputAddresses.length === 0) {
      return;
    }

    let searchedMarkets = [] as DiscoveryInputMarket[];
    let isSearchContainsUSA: boolean = false;
    let searchedAirbticsProCountry: string | null = null;
    let searchRegionCountryNames: string[] = [];

    try {
      setGeocodingLoading(true);
      await Promise.all(
        inputAddresses.map(async inputAddress => {
          const [lat, lng] = (await geocodeAddress(inputAddress)) as [number, number];
          const currentCountry = splitDistrictAndCountryFromAddress(inputAddress).country;

          searchRegionCountryNames.push(currentCountry);

          if (currentCountry === 'USA') {
            isSearchContainsUSA = true;
          } else if (
            !searchedAirbticsProCountry &&
            Object.keys(airbticsProCountries).includes(currentCountry?.toUpperCase())
          ) {
            searchedAirbticsProCountry = airbticsProCountries[currentCountry?.toUpperCase()];
          }

          searchedMarkets = [
            ...searchedMarkets,
            {
              district: splitDistrictAndCountryFromAddress(inputAddress).district,
              country: currentCountry,
              centerLat: lat,
              centerLng: lng,
            },
          ];
        })
      );
      setGeocodingLoading(false);
      if (!isSearchContainsUSA && searchedAirbticsProCountry) {
        window.location.href = '/discovery/' + searchedAirbticsProCountry;
      }
      // isSearchContainsUSA ? setIsCurrentCountryUS(true) : setIsCurrentCountryUS(false);
      setCurrentCountry(findAirbticsProCountryCode(searchRegionCountryNames, true, true));
    } catch (error) {
      console.log('error in discovery geocoding ', error);
    }
    const points = searchedMarkets.map(point => ({ lat: point.centerLat, lng: point.centerLng }));
    setCentroid(getCentroid(points));
    setInputMarkets(searchedMarkets);
  }, []);

  const setPolygon = (
    map,
    maps,
    paths: Coordinates[][],
    strokeColor: string,
    fillColor: string
  ) => {
    const polygon = new maps.Polygon({
      paths: paths,
      strokeColor: strokeColor,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: fillColor,
      fillOpacity: 0.12,
    });
    polygon.setMap(map);

    return polygon;
  };

  const showPolygon = (polygon, map) => {
    polygon.setMap(map);
  };

  const hidePolygon = polygon => {
    polygon.setMap(null);
  };

  const setPolygonColor = (polygon, strokeColor: string, fillColor: string) => {
    polygon.setOptions({
      strokeColor: strokeColor,
      fillColor: fillColor,
    });
  };

  const getPolygonPaths = (polygonRaw: string, multiPolygonRaw: string) => {
    const polygonArr: Coordinates[][] = processPolygon(polygonRaw, multiPolygonRaw);
    let paths: Coordinates[][] = [];
    if (polygonArr.length > 0) {
      polygonArr.forEach((polygon: Coordinates[], _: number) => paths.push(polygon));
    }
    return paths;
  };

  useEffect(() => {
    if (
      !nearbyRecommendedMarkets.loading &&
      nearbyRecommendedMarkets.data &&
      nearbyRecommendedMarkets.data.length > 0 &&
      mapCopy &&
      polygonCopies
    ) {
      nearbyRecommendedMarkets.data.forEach((nearbyRecommendedMarket, index: number) => {
        const polygonCopy = polygonCopies[nearbyRecommendedMarket.marketID];
        if (polygonCopy) {
          if (nearbyRecommendedMarket.visible || !filtersApplied) {
            showPolygon(polygonCopy, mapCopy);
          } else {
            hidePolygon(polygonCopy);
          }
        }
      });
    }
  }, [nearbyRecommendedMarkets.data, nearbyRecommendedMarkets.loading, mapCopy, filtersApplied]);

  const apiIsLoaded = (map, maps) => {
    setMapCopy(map);
    let marketRecommendations = [] as any;
    let polygonRefs = {} as any;
    if (nearbyRecommendedMarkets.data && nearbyRecommendedMarkets.data.length > 0) {
      nearbyRecommendedMarkets.data.forEach((nearbyRecommendedMarket, index: number) => {
        if (rows.map(row => row.marketID).includes(nearbyRecommendedMarket.marketID)) {
          const nearbyRecommendedMarketPaths = getPolygonPaths(
            nearbyRecommendedMarket.polygon,
            nearbyRecommendedMarket.multiPolygon
          );
          const nearbyRecommendedMarketHighlightDistrict = setPolygon(
            map,
            maps,
            nearbyRecommendedMarketPaths,
            COLOR_PRIMARY,
            MAP_FILL_COLOR
          );
          polygonRefs[nearbyRecommendedMarket.marketID] = nearbyRecommendedMarketHighlightDistrict;

          // handle hover
          maps.event.addListener(nearbyRecommendedMarketHighlightDistrict, 'mouseover', () => {
            setPolygonColor(
              nearbyRecommendedMarketHighlightDistrict,
              COLOR_PRIMARY_DARK,
              MAP_FILL_COLOR
            );
          });
          maps.event.addListener(nearbyRecommendedMarketHighlightDistrict, 'mouseout', () => {
            setPolygonColor(
              nearbyRecommendedMarketHighlightDistrict,
              COLOR_PRIMARY,
              MAP_FILL_COLOR
            );
          });

          // handle click
          maps.event.addListener(nearbyRecommendedMarketHighlightDistrict, 'click', () => {
            handleMarketRecommendationClick(nearbyRecommendedMarket);
          });
          marketRecommendations.push(nearbyRecommendedMarketHighlightDistrict);
        }
      });

      setPolygonCopies(polygonRefs);
      // adjust bounds to fit all polygons
      marketRecommendations.length > 0 &&
        applySmartBounds(map, maps, {} as Bounds, marketRecommendations);
    }
  };

  const marketRecommendationMarkers = useMemo(() => {
    if (
      !nearbyRecommendedMarkets.loading &&
      nearbyRecommendedMarkets.data &&
      nearbyRecommendedMarkets.data.length > 0
    ) {
      return nearbyRecommendedMarkets.data.map(
        (item: NearbyRecommendedMarket, index: number) =>
          (!filtersApplied || item.visible) &&
          rows.map(row => row.marketID).includes(item.marketID) && (
            <MarketRecommendationPointer
              key={index}
              lat={item.lat}
              lng={item.lng}
              data={item}
              searchedAddress={searchBarValue[0]}
              isTopMarket={index < 5}
              isHighlighted={
                hoveredRow.center
                  ? hoveredRow.center.lat === item.lat && hoveredRow.center.lng === item.lng
                  : false
              }
            />
          )
      );
    }
    return;
  }, [nearbyRecommendedMarkets, hoveredRow.center, rows, filtersApplied]);

  const searchedCoordMarkers = useMemo(() => {
    return inputMarkets.map((inputMarket, index: number) => (
      <RoomIconMarker key={index} lat={inputMarket.centerLat} lng={inputMarket.centerLng} />
    ));
  }, [inputMarkets]);

  useEffect(() => {
    if (
      nearbyRecommendedMarkets &&
      nearbyRecommendedMarkets.notSupportedSearches &&
      nearbyRecommendedMarkets.notSupportedSearches.length > 0 &&
      !airbticsProCountries[currentCountry] &&
      isFromWordpress != WORDPRESS_TO_DISCOVERY_KEY &&
      isUserLoggedIn
    ) {
      setSearchNotSupportedWarningModalOpen(true);
      if (inputMarkets.length === 1) {
        setRows([]);
      }
    }
  }, [nearbyRecommendedMarkets.notSupportedSearches]);

  const scheduleDemo = () => {
    window.open('https://calendly.com/joe-airbtics/15min', 'nofollow');
  };

  const handleSearchNotSupportedModalClose = () => {
    setSearchNotSupportedWarningModalOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Market Name',
      width: 150,
    },
    {
      field: 'bedrooms',
      headerName: 'Bedrooms',
      type: 'number',
      width: 100,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'size',
      headerName: 'Market Size',
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            width: '100px',
            height: '30px',
            borderRadius: '30px',
            background: getColorFromSizeLevel(DiscoveryTableMarketSizeMap.indexOf(params.value)),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              textAlign: 'center',
              color: COLOR_WHITE,
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'Market Type',
      minWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        if (params.value) {
          return (
            <Box
              sx={{
                width: '100px',
                height: '30px',
                borderRadius: '30px',
                background: getMarketTypeColor(params.value),
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  color: COLOR_WHITE,
                }}
              >
                {params.value}
              </Typography>
            </Box>
          );
        } else return 'N/A';
      },
    },
    {
      field: 'grossRentalYield',
      headerName: 'Gross Rental Yield',
      type: 'number',
      minWidth: 100,
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        if (params.value == null) {
          return '';
        }
        return `${params.value} %`;
      },
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'annualRevenue',
      headerName: 'Annual Revenue',
      type: 'number',
      minWidth: 100,
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        if (params.value == null) {
          return '';
        }
        const formattedValue = formatCurrency(
          convertCurrency(currency, params.value).toLocaleString()
        );
        return `${exchangeRates[currency].symbol}${formattedValue}`;
      },
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'distance',
      headerName: 'Distance',
      type: 'number',
      minWidth: 100,
      flex: 2,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {params.value} {params.row.distanceUnit} from {params.row.distanceFrom}
        </Typography>
      ),
      align: 'left',
      headerAlign: 'left',
    },
  ];

  const getRows = (markets: NearbyRecommendedMarket[], user: User) => {
    if (!user.isSubscribed && !areArraysEqual(inputMarkets, [sampleMarket])) {
      let sampleTableData: DiscoveryTableData[] = [];
      for (let i = 0; i < 23; i++) {
        if (i < 3) {
          const market = markets[i];
          market &&
            sampleTableData.push({
              id: i + 1,
              name: `${market.district}, ${market.city}`,
              bedrooms: market.investmentOptions[0].bedrooms,
              size: getMarketSize(market.totalSupply),
              type: market.marketType,
              grossRentalYield: market.investmentOptions[0].grossRentalYield,
              annualRevenue: market.investmentOptions[0].annualRevenue,
              distance: market.countryCode === 'US' ? market.distanceInMiles : market.distanceInKM,
              distanceUnit: market.countryCode === 'US' ? 'mi' : 'km',
              distanceFrom: market.distanceFrom.split(',')[0],
              lat: market.lat,
              lng: market.lng,
              marketID: market.marketID,
            } as DiscoveryTableData);
        } else {
          // fake teaser data
          sampleTableData.push({
            id: i + 1,
            name: 'Sample',
            bedrooms: 2,
            size: getMarketSize(null),
            type: null,
            grossRentalYield: 20,
            annualRevenue: 100000,
            distance: 5,
            distanceUnit: 'mi',
            distanceFrom: 'Sample',
            lat: 0,
            lng: 0,
            marketID: 123,
          } as DiscoveryTableData);
        }
      }
      return sampleTableData;
    }

    let index = 1;
    let tableData: DiscoveryTableData[] = [];
    markets.forEach(market => {
      market.investmentOptions.forEach(investmentOption => {
        tableData.push({
          id: index++,
          name: `${market.district}, ${market.city}`,
          bedrooms: investmentOption.bedrooms,
          size: getMarketSize(market.totalSupply),
          type: market.marketType,
          grossRentalYield: investmentOption.grossRentalYield,
          annualRevenue: investmentOption.annualRevenue,
          distance: market.countryCode === 'US' ? market.distanceInMiles : market.distanceInKM,
          distanceUnit: market.countryCode === 'US' ? 'mi' : 'km',
          distanceFrom: market.distanceFrom.split(',')[0],
          lat: market.lat,
          lng: market.lng,
          marketID: market.marketID,
        } as DiscoveryTableData);
      });
    });
    return tableData;
  };

  const findAirbticsProCountryCode = (
    countryNameArray: string[],
    returnCountryName: boolean = false,
    noDefaultCountry: boolean = false
  ) => {
    for (const countryName of countryNameArray) {
      if (Object.keys(airbticsProCountries).includes(countryName.toUpperCase())) {
        return returnCountryName
          ? countryName.toUpperCase()
          : airbticsProCountries[countryName.toUpperCase()];
      }
    }
    return noDefaultCountry ? '' : returnCountryName ? 'USA' : 'US';
  };

  const getRowClassName = (params: GridRowParams) => {
    if (params.row.id >= 4 && !user.isSubscribed && !areArraysEqual(inputMarkets, [sampleMarket])) {
      return 'blurred-row';
    }
    return '';
  };

  const DataGridRowPopover = () => {
    const apiRef = useGridApiContext();

    const handleRowMouseEnter: GridEventListener<'rowMouseEnter'> = (
      params, // GridRowParams
      event, // MuiEvent<React.MouseEvent<HTMLElement>>
      details // GridCallbackDetails
    ) => {
      setHoveredRow(_ => ({
        center: {
          lat: params.row.lat,
          lng: params.row.lng,
        },
        anchorEl: event.currentTarget,
      }));
      setDistanceTooltipText(
        `${params.row.distance} ${params.row.distanceUnit} from ${params.row.distanceFrom}`
      );
    };

    useGridApiEventHandler(apiRef, 'rowMouseEnter', handleRowMouseEnter);

    return (
      <Popover
        open={Boolean(hoveredRow.anchorEl)}
        anchorEl={hoveredRow.anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        sx={{ pointerEvents: 'none' }}
      >
        <div
          style={{
            height: '40px',
            padding: '0 20px',
            background: BACKGROUND_COLOR,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'row',
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Go to Market
          </Typography>
          <OpenInNewIcon sx={{ width: '15px', height: '15px', marginLeft: 1 }} />
          <RoomIcon
            sx={{
              width: '15px',
              height: '15px',
              marginLeft: 2,
              marginRight: 1,
              color: COLOR_ORANGE,
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {distanceTooltipText}
          </Typography>
        </div>
      </Popover>
    );
  };

  useEffect(() => {
    if (
      !nearbyRecommendedMarkets.loading &&
      nearbyRecommendedMarkets.data &&
      nearbyRecommendedMarkets.data.length > 0
    ) {
      setRows(getRows(nearbyRecommendedMarkets.data, user));
    }
  }, [nearbyRecommendedMarkets.data, nearbyRecommendedMarkets.loading]);

  const handleFilterBtnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const exportData = () => {
    let dataToExport = filtersApplied ? filteredRows : rows;
    dataToExport = dataToExport.map(data => {
      const district = data.name
        .split(', ')[0]
        .toLowerCase()
        .replaceAll(' ', '%20');
      const city = data.name
        .split(', ')[1]
        .toLowerCase()
        .replaceAll(' ', '%20');
      return {
        ...data,
        name: data.name.replace(',', ' (') + ')',
        link: `https://app.airbtics.com/airbnb-data/united-states/${city}/${district}`,
      };
    });
    makeDownloadableBlob(dataToExport, `High_Demand_Airbnb_Markets_${currency}`);
  };

  const showHelp = () => {
    window.open(DISCOVERY_TOOL_VIDEO_TUTORIAL_LINK, '_blank');
  };

  const handleBedroomsFilterRangeChange = (event: Event, newValue: number | number[]) => {
    setBedroomsFilterRange(bedroomsFilterRange => {
      return newValue as number[];
    });
  };

  const handleDistanceFilterRangeChange = (event: Event, newValue: number | number[]) => {
    setDistanceFilterRange(distanceFilterRange => {
      return newValue as number[];
    });
  };

  const handleMarketSizeFilterChange = (newValue: string) => {
    setMarketSizeFilter(marketSizeFilter => {
      const newMarketSizeFilter = { ...marketSizeFilter, [newValue]: !marketSizeFilter[newValue] };
      return newMarketSizeFilter;
    });
  };

  const handleMarketTypeFilterChange = (newValue: string) => {
    setMarketTypeFilter(marketTypeFilter => {
      const newMarketTypeFilter = { ...marketTypeFilter, [newValue]: !marketTypeFilter[newValue] };
      return newMarketTypeFilter;
    });
  };

  const applyFilters = () => {
    setFiltersApplied(true);
    setFilteredRows(e => {
      let filteredRows: DiscoveryTableData[] = [];
      let filteredRowsMarketIDs: number[] = [];
      let filteredUniqueMarketIDs = new Set<number>();
      filteredRows = rows.filter(row => {
        if (
          row.bedrooms >= bedroomsFilterRange[0] &&
          row.bedrooms <= bedroomsFilterRange[1] &&
          row.distance >= distanceFilterRange[0] &&
          row.distance <= distanceFilterRange[1] &&
          Object.keys(marketSizeFilter)
            .filter(key => marketSizeFilter[key])
            .includes(row.size) &&
          Object.keys(marketTypeFilter)
            .filter(key => marketTypeFilter[key])
            .includes(row.type as string)
        ) {
          filteredUniqueMarketIDs.add(row.marketID);
          return row;
        }
        return;
      });
      filteredRowsMarketIDs = [...filteredUniqueMarketIDs];
      const filteredMarkets = nearbyRecommendedMarkets.data.map(market =>
        filteredRowsMarketIDs.includes(market.marketID)
          ? { ...market, visible: true }
          : { ...market, visible: false }
      );
      dispatch(
        getNearbyRecommendedMarketsLoadingEnd({
          message: filteredMarkets,
          notSupportedSearches: nearbyRecommendedMarkets.notSupportedSearches,
        })
      );
      return filteredRows;
    });
    setAnchorEl(null);
  };

  const clearFilters = () => {
    setFiltersApplied(false);
    setAnchorEl(null);
  };

  const searchBar = (isOnboardingOn: boolean = false) => {
    return (
      <SearchbarAutocomplete
        id="discoverSearch"
        multipleSearch={true}
        multipleSearchLimit={5}
        shrink={true}
        open={open}
        setOpen={setOpen}
        loading={loading}
        options={options}
        defaultValue={isOnboardingOn ? [] : [sampleDiscoveryAddress]}
        value={searchBarValue}
        label="Select up to 5 Areas to explore the most profitable Airbnb locations"
        handleInputChange={handleInputChange}
        handleSearchChange={handleSearchChange}
        isEmptySearchbar={emptySearchBar}
        inputElementStyle={{
          backgroundColor: COLOR_WHITE,
          borderRadius: '10px',
          border: '2px solid rgba(58, 175, 169, 0.54)',
          borderFocused: `2px solid ${COLOR_PRIMARY}`,
          fontSize: '16px',
          fontWeight: 500,
          chipBackgroundColor: BACKGROUND_COLOR,
        }}
        inputLabelStyle={{
          color: COLOR_PRIMARY,
          colorFocused: COLOR_PRIMARY,
          fontWeight: 500,
        }}
        handleOnMouseEnter={() => {
          setSearchbarFocus(true);
        }}
        handleOnMouseLeave={() => {
          setSearchbarFocus(false);
        }}
        endAdorement={
          <IconButton
            onClick={() => discoverMarkets(searchBarValue)}
            sx={{
              background: COLOR_PRIMARY,
              '&:hover': {
                background: COLOR_PRIMARY,
              },
              padding: searchbarFocus ? '8px 15px' : '8px',
              borderRadius: '20px',
            }}
          >
            <SearchOutlinedIcon sx={{ color: 'white' }} />
            <Typography
              sx={{
                color: 'white',
                display: searchbarFocus ? 'block' : 'hidden',
                fontWeight: 500,
                opacity: searchbarFocus ? 1 : 0,
                width: searchbarFocus ? '60px' : '0px',
                transition: 'all 0.3s ease-in-out',
              }}
            >
              Search
            </Typography>
          </IconButton>
        }
      />
    );
  };

  const onSamplePageClick = () => {
    setSearchBarValue([sampleDiscoveryAddress]);
    setInputMarkets([sampleMarket]);
  };

  if (['UK', 'CA', 'AU'].includes(airbticsProCountryCode)) {
    return (
      <AirbticsProDiscovery
        currency={currency}
        setCurrency={setCurrency}
        airbticsProCountryCode={airbticsProCountryCode}
        isMdScreenSize={isMdScreenSize}
      />
    );
  }

  if (inputMarkets.length === 0) {
    console.log('User Country Code: ', userCountryCode);

    return (
      <>
        <IndividualOnBoardingPage
          SearchBox={searchBar}
          onSamplePageClick={onSamplePageClick}
          onVideoClick={() => {
            window.open('https://youtu.be/qrMX_z5LXLU', '_blank');
          }}
          cover={greyMapBackground}
        />

        {/* {isLoggedIn && <RedirectModal dashboardName='Discovery' fixDate={20} />} */}

        {searchNotSupportedWarningModalOpen && (
          <DecisionDialog
            open={searchNotSupportedWarningModalOpen}
            handleClose={handleSearchNotSupportedModalClose}
            title="Oops! Currently, this feature only supports the US market"
            descriptions={[
              `Our team is still working on the UK, Canada, Australia & Spain markets and will be available soon. We will notify you once we release this feature. For now, you may view the available Airbnb statistics of these countries.`,
            ]}
            agreeButtonText="See Market Data"
            disagreeButtonText="Close"
            onAgree={() => window.open('/airbnb-data', '_self')}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Find the highest demand Airbnb markets around your area</title>
      </Helmet>

      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={{ minHeight: '80vh', padding: isMdScreenSize ? '20px' : '40px' }}
      >
        <Grid
          item
          container
          xs={12}
          md={8}
          lg={9}
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={2}
        >
          <Grid
            item
            xs={12}
            container
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Grid
              item
              xs={12}
              md={12}
              lg={8}
              sx={{
                paddingBottom: isMdScreenSize ? 2 : 0,
              }}
            >
              {searchBar()}
            </Grid>

            <Grid item>
              <OutlinedRectangularButton
                height="40px"
                width="100px"
                colors={{
                  backgroundColor: COLOR_WHITE,
                  color: COLOR_PRIMARY_DARK,
                  borderColor: 'rgba(58, 175, 169, 0.54)',
                }}
                icon={filtersApplied ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                text="Filter"
                handleOnClick={handleFilterBtnClick}
                isFocused={filterOpen}
              />
              <Popover
                open={filterOpen}
                anchorEl={anchorEl}
                onClose={handleFilterClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <Box
                  sx={{
                    width: 300,
                    padding: '20px 25px',
                  }}
                >
                  <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>Bedrooms 🛏️</Typography>
                  <Slider
                    size="small"
                    value={bedroomsFilterRange}
                    min={1}
                    max={5}
                    step={1}
                    marks={[
                      {
                        value: 1,
                        label: '1',
                      },
                      {
                        value: 5,
                        label: '5',
                      },
                    ]}
                    onChange={handleBedroomsFilterRangeChange}
                    valueLabelDisplay="auto"
                    sx={{
                      color: COLOR_PRIMARY,
                    }}
                  />

                  <Typography sx={{ fontSize: '16px', fontWeight: 'bold', marginTop: 2 }}>
                    Distance 🚗
                  </Typography>
                  <Slider
                    size="small"
                    value={distanceFilterRange}
                    min={0}
                    max={700}
                    step={50}
                    marks={[
                      {
                        value: 0,
                        label: '0',
                      },
                      {
                        value: 700,
                        label: '700',
                      },
                    ]}
                    onChange={handleDistanceFilterRangeChange}
                    valueLabelDisplay="auto"
                    sx={{
                      color: COLOR_PRIMARY,
                    }}
                  />

                  <Typography
                    sx={{ fontSize: '16px', fontWeight: 'bold', marginTop: 2, marginBottom: 1 }}
                  >
                    Market Size
                  </Typography>

                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                  >
                    <Grid item>
                      <SelecFilterChip
                        height="30px"
                        width="85px"
                        borderRadius="30px"
                        colors={{
                          backgroundColor: COLOR_WHITE,
                          color: getColorFromSizeLevel(
                            DiscoveryTableMarketSizeMap.indexOf('small')
                          ),
                          borderColor: getColorFromSizeLevel(
                            DiscoveryTableMarketSizeMap.indexOf('small')
                          ),
                        }}
                        text="SMALL"
                        fontWeight="bold"
                        handleOnClick={() => handleMarketSizeFilterChange('small')}
                        isSelected={marketSizeFilter.small}
                        tooltip="Market with less than 500 listings"
                      />
                    </Grid>
                    <Grid item>
                      <SelecFilterChip
                        height="30px"
                        width="85px"
                        borderRadius="30px"
                        colors={{
                          backgroundColor: COLOR_WHITE,
                          color: getColorFromSizeLevel(
                            DiscoveryTableMarketSizeMap.indexOf('medium')
                          ),
                          borderColor: getColorFromSizeLevel(
                            DiscoveryTableMarketSizeMap.indexOf('medium')
                          ),
                        }}
                        text="MEDIUM"
                        fontWeight="bold"
                        handleOnClick={() => handleMarketSizeFilterChange('medium')}
                        isSelected={marketSizeFilter.medium}
                        tooltip="Market with 500 - 2500 listings"
                      />
                    </Grid>
                    <Grid item>
                      <SelecFilterChip
                        height="30px"
                        width="85px"
                        borderRadius="30px"
                        colors={{
                          backgroundColor: COLOR_WHITE,
                          color: getColorFromSizeLevel(DiscoveryTableMarketSizeMap.indexOf('big')),
                          borderColor: getColorFromSizeLevel(
                            DiscoveryTableMarketSizeMap.indexOf('big')
                          ),
                        }}
                        text="BIG"
                        fontWeight="bold"
                        handleOnClick={() => handleMarketSizeFilterChange('big')}
                        isSelected={marketSizeFilter.big}
                        tooltip="Market with greater than 2500 listings"
                      />
                    </Grid>
                  </Grid>

                  <Typography
                    sx={{ fontSize: '16px', fontWeight: 'bold', marginTop: 2, marginBottom: 1 }}
                  >
                    Market Size
                  </Typography>

                  <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={1}
                  >
                    <Grid item>
                      <SelecFilterChip
                        height="30px"
                        width="85px"
                        borderRadius="30px"
                        colors={{
                          backgroundColor: COLOR_WHITE,
                          color: getMarketTypeColor('vacation'),
                          borderColor: getMarketTypeColor('vacation'),
                        }}
                        text="VACATION"
                        fontWeight="bold"
                        handleOnClick={() => handleMarketTypeFilterChange('vacation')}
                        isSelected={marketTypeFilter.vacation}
                        tooltip="Market with less than 500 listings"
                      />
                    </Grid>
                    <Grid item>
                      <SelecFilterChip
                        height="30px"
                        width="85px"
                        borderRadius="30px"
                        colors={{
                          backgroundColor: COLOR_WHITE,
                          color: getMarketTypeColor('urban'),
                          borderColor: getMarketTypeColor('urban'),
                        }}
                        text="URBAN"
                        fontWeight="bold"
                        handleOnClick={() => handleMarketTypeFilterChange('urban')}
                        isSelected={marketTypeFilter.urban}
                        tooltip="Market with 500 - 2500 listings"
                      />
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Grid item sx={{ marginTop: 5 }}>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          color: filtersApplied ? 'black' : COLOR_GREY,
                        }}
                        onClick={clearFilters}
                      >
                        Clear All Filters
                      </Typography>
                    </Grid>
                    <Grid item sx={{ marginTop: 5 }}>
                      <OutlinedRectangularButton
                        height="40px"
                        width="140px"
                        colors={{
                          backgroundColor: COLOR_WHITE,
                          color: COLOR_PRIMARY_DARK,
                          borderColor: COLOR_PRIMARY_DARK,
                        }}
                        text="APPLY FILTERS"
                        handleOnClick={applyFilters}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Popover>
            </Grid>

            <Grid item>
              <OutlinedRectangularButton
                height="40px"
                width="100px"
                colors={{
                  backgroundColor: COLOR_WHITE,
                  color: COLOR_PRIMARY_DARK,
                  borderColor: 'rgba(58, 175, 169, 0.54)',
                }}
                icon={<DownloadOutlinedIcon />}
                text="Export"
                handleOnClick={exportData}
              />
            </Grid>

            <Tooltip title="Video Tutorial">
              <Grid item>
                <OutlinedRectangularButton
                  height="40px"
                  width="40px"
                  colors={{
                    backgroundColor: COLOR_WHITE,
                    color: '#332FFF',
                    borderColor: '#332FFF',
                  }}
                  icon={<QuestionMarkOutlinedIcon />}
                  handleOnClick={showHelp}
                />
              </Grid>
            </Tooltip>
          </Grid>

          {/* {isCurrentCountryUS && ( */}
          <Grid item container xs={12}>
            <Box
              sx={{ height: 550, width: '100%', position: 'relative' }}
              onMouseLeave={() => setHoveredRow(prev => ({ center: prev.center, anchorEl: null }))}
            >
              {nearbyRecommendedMarkets.loading ? (
                <LoadingCard height={550} borderRadius="20px" />
              ) : (
                <DataGrid
                  rows={filtersApplied ? filteredRows : rows}
                  columns={columns}
                  checkboxSelection={false}
                  disableSelectionOnClick
                  experimentalFeatures={{ newEditingApi: true }}
                  hideFooter={true}
                  disableColumnMenu={true}
                  onRowClick={params =>
                    window.open(
                      `https://app.airbtics.com/airbnb-data/united-states/${params.row.name
                        .split(', ')[1]
                        .toLowerCase()}/${params.row.name
                        .split(', ')[0]
                        .replaceAll(' ', '%20')
                        .toLowerCase()}`,
                      '_blank'
                    )
                  }
                  sx={{
                    background: COLOR_WHITE,
                    borderRadius: '20px',
                    padding: '10px 20px',
                    '& .MuiDataGrid-iconSeparator': {
                      display: 'none',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      color: COLOR_PRIMARY_DARK,
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-cellContent': {
                      fontWeight: 500,
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: 'rgba(93, 82, 107)', //rgba(167, 113, 255)
                      cursor: 'pointer',
                    },
                  }}
                  components={{ BasePopper: DataGridRowPopover }}
                  getRowClassName={getRowClassName}
                />
              )}

              {!user.isSubscribed &&
                !nearbyRecommendedMarkets.loading &&
                !areArraysEqual(inputMarkets, [sampleMarket]) && (
                  <SubscriptionPromptOverlay
                    isUserLoggedIn={isUserLoggedIn}
                    setSubscriptionModalOpen={setSubscriptionModalOpen}
                    setLoginModalOpen={setLoginModalOpen}
                  />
                )}
            </Box>
          </Grid>
          {/* )} */}
        </Grid>

        <Grid item xs={12} md={4} lg={3}>
          {nearbyRecommendedMarkets.loading ? (
            <LoadingCard height="80vh" borderRadius="20px" />
          ) : (
            <div className={classes.mapContainer}>
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: GOOGLE_MAPS_API_KEY,
                  libraries: ['drawing', 'places', 'visualization'],
                  id: 'discoverDashboard',
                }}
                center={
                  hoveredRow.center
                    ? { lat: hoveredRow.center.lat, lng: hoveredRow.center.lng }
                    : { lat: centroid.lat, lng: centroid.lng }
                }
                options={mapOptions}
                zoom={hoveredRow.center ? 13 : 12}
                resetBoundsOnResize
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps)}
              >
                {!nearbyRecommendedMarkets.loading &&
                  nearbyRecommendedMarkets.data &&
                  nearbyRecommendedMarkets.data.length > 0 &&
                  marketRecommendationMarkers}
                {searchedCoordMarkers}
              </GoogleMapReact>
            </div>
          )}
        </Grid>

        {currentCountry === 'USA' && (
          <Grid container xs={12} sx={{ padding: isMdScreenSize ? 0 : '20px' }}>
            <AirbticsProDiscovery
              currency={currency}
              setCurrency={setCurrency}
              airbticsProCountryCode={airbticsProCountryCode}
              isMdScreenSize={isMdScreenSize}
            />
          </Grid>
        )}

        {/* {isLoggedIn && <RedirectModal dashboardName='Discovery' fixDate={20} />} */}
      </Grid>

      {/* <AirbticsProDiscovery
        currency={currency}
        setCurrency={setCurrency}
        currentCountry={currentCountry}
        countryParamsValue={countryParamsValue}
      /> */}

      {searchNotSupportedWarningModalOpen && (
        <DecisionDialog
          open={searchNotSupportedWarningModalOpen}
          handleClose={handleSearchNotSupportedModalClose}
          title="Oops! Currently, this feature only supports the US market"
          descriptions={[
            `Our team is still working on the UK, Canada, Australia & Spain markets and will be available soon. We will notify you once we release this feature. For now, you may view the available Airbnb statistics of these countries.`,
          ]}
          agreeButtonText={`View Demo`}
          disagreeButtonText="Search This Market"
          // onDisagree={() => window.open('/', '_self')}
          onDisagree={() => handleMarketSidebarClick('addMarket')}
          onAgree={() => window.open('/discovery?search=Miami, FL, USA', '_self')}
        />
      )}
    </>
  );
};

export default DiscoveryDashboard;

const getMarketSize = (supply: number | null) => {
  if (!supply) {
    return null;
  }
  if (supply < 500) {
    return 'small';
  } else if (supply < 2500) {
    return 'medium';
  } else {
    return 'big';
  }
};

const getMarketTypeColor = (type: 'urban' | 'vacation' | null) => {
  if (!type) {
    return LIGHT_GREY;
  }
  if (type === 'urban') {
    return '#29277B';
  } else {
    return '#FFC700';
  }
};

const splitDistrictAndCountryFromAddress = (address: string) => {
  const country = [...address.split(', ')].pop() as string;
  const district = address.replace(`, ${country}`, '');
  return {
    district,
    country,
  };
};
